import { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';

const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    const isDismissed = localStorage.getItem('pwa_banner_dismissed');
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    setIsIOS(isIOSDevice);

    // If already installed or dismissed, don't show
    if (isInstalled || isDismissed) {
      return;
    }

    // For iOS Safari (doesn't support beforeinstallprompt)
    if (isIOSDevice && !navigator.standalone) {
      // Show banner after 3 seconds on iOS
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For Android/Desktop browsers
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after 3 seconds
      setTimeout(() => {
        setShowBanner(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setShowBanner(false);
      setDeferredPrompt(null);
      localStorage.setItem('pwa_installed', 'true');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      return;
    }

    // Show the native install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      localStorage.setItem('pwa_installed', 'true');
    }
    
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa_banner_dismissed', 'true');
  };

  if (!showBanner) {
    return null;
  }

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Instalar ParkingSpot no iPhone
            </h3>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-gray-100 rounded-full"
              aria-label="Fechar"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-[#302681] text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900">Toque no botão Compartilhar</p>
                <div className="flex items-center mt-1 text-gray-600">
                  <Share className="w-5 h-5 mr-1" />
                  <span className="text-sm">Na barra inferior do Safari</span>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-[#302681] text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900">Role e toque em "Adicionar à Tela de Início"</p>
                <div className="flex items-center mt-1 text-gray-600">
                  <Plus className="w-5 h-5 mr-1" />
                  <span className="text-sm">Pode precisar rolar para encontrar</span>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-[#302681] text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900">Toque em "Adicionar"</p>
                <p className="text-sm text-gray-600 mt-1">O app aparecerá na sua tela inicial</p>
              </div>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="mt-6 w-full py-3 bg-[#302681] text-white rounded-xl font-semibold hover:bg-[#251d63] transition"
          >
            Entendi!
          </button>
        </div>
      </div>
    );
  }

  // Main Install Banner
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-[#302681] to-[#831D1C] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-2xl">🅿️</span>
              </div>
              <div className="text-white">
                <h3 className="font-bold text-lg">Instalar ParkingSpot</h3>
                <p className="text-sm opacity-90">Acesso rápido na sua tela inicial</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-white/10 rounded-full transition"
              aria-label="Fechar"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="p-4 flex items-center justify-between bg-gray-50">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>✓ Funciona offline</span>
            <span>•</span>
            <span>✓ Mais rápido</span>
          </div>
          <button
            onClick={handleInstallClick}
            className="flex items-center space-x-2 px-6 py-2.5 bg-[#009440] text-white rounded-xl font-semibold hover:bg-[#007a35] transition shadow-lg"
            data-testid="pwa-install-button"
          >
            <Download className="w-4 h-4" />
            <span>Instalar</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
